import { Screen, SCREEN_VALUES } from '../contexts/AppContext';

/**
 * Screens that only make sense BEFORE a user is authenticated (or, for
 * invitation-acceptance, before they've joined the specific business the
 * invite is for). Everything else in the Screen union is fair game for an
 * authenticated user to deep-link/refresh into — this is deliberately a
 * short, exhaustively-reviewed denylist rather than the old pattern of an
 * allowlist that had to be manually kept in sync with the ~109-value Screen
 * union (see AppNavigator.tsx's old `allowedAuthenticatedScreens`, which
 * only ever covered a subset and silently dropped users back to 'welcome'
 * on refresh for any screen it hadn't been updated to include).
 *
 * Derived from the render switch's pre-auth-only `case` labels in
 * AppNavigator.tsx (welcome/login/unified-login/business-picker/register/
 * otp-verification/forgot-password/reset-password/invitation-acceptance),
 * plus the mid-onboarding screens (business-creation-wizard/plan-selection/
 * channel-onboarding-stepper) that also require no prior auth/business.
 */
export const PRE_AUTH_ONLY_SCREENS: ReadonlySet<Screen> = new Set([
  'welcome',
  'login',
  'unified-login',
  'business-picker',
  'business-creation-wizard',
  'plan-selection',
  'channel-onboarding-stepper',
  'register',
  'otp-verification',
  'forgot-password',
  'reset-password',
  'invitation-acceptance',
]);

export const isPreAuthOnlyScreen = (screen: string): boolean => PRE_AUTH_ONLY_SCREENS.has(screen as Screen);

/**
 * Screens reachable with no authentication AND no business context — public
 * pages shared via a link (order tracking, a public catalog/product page,
 * WhatsApp calendar booking, checkout/cart for a public storefront, the
 * Facebook OAuth callback redirect target). Distinct from
 * PRE_AUTH_ONLY_SCREENS: these are also fine for an authenticated user to
 * land on (no denylist entry needed there), they're just additionally OK
 * without auth too.
 */
export const PUBLIC_SCREENS: ReadonlySet<Screen> = new Set([
  'facebook-callback',
  'calendar-booking',
  'product-view',
  'catalog-view',
  'cart',
  'checkout',
  'order-success',
  'track-order',
]);

/**
 * Everything else — screens that require a signed-in user, blocked for an
 * unauthenticated visitor (redirected to 'welcome'). Derived as the
 * complement of the two allowlists above rather than hand-maintained, so a
 * new Screen value is safely protected-by-default instead of silently
 * falling through to "allow" the way the old hand-written protectedScreens
 * array did for anything it hadn't been updated to include.
 */
export const isAuthRequiredScreen = (screen: string): boolean =>
  SCREEN_VALUES.includes(screen as Screen) && !isPreAuthOnlyScreen(screen) && !PUBLIC_SCREENS.has(screen as Screen);
